import { TemplateScene } from '@/types/cmsTypes';
import { Card } from '@/components/ui/card';
import { CardTitle } from '@/components/ui/text';
import { DIFFICULTY_ICON, DIFFICULTY_PILL, PILL_BASE } from '@/components/cms/ui/pill-styles';
import projectPlaceholder from '@/assets/project-historical.jpg';
import { getSquiggleCropStyle } from '@/utils/squiggleCrop';

interface TemplateSceneCardProps {
  template: TemplateScene;
  onSelect: (template: TemplateScene) => void;
}

export function TemplateSceneCard({ template, onSelect }: TemplateSceneCardProps) {
  // Check if the hero image is the placeholder
  const isPlaceholderImage = template.heroImage === projectPlaceholder;
  

  return (
    <Card elevation="high"
      padding="none"
      interactive
      className="group flex h-full flex-col overflow-hidden rounded-lureo-radius-xl"
      onClick={() => onSelect(template)}>
      <div className="relative mb-2 overflow-hidden sm:mb-4">
        {isPlaceholderImage ? (
          // Use squiggle image with different crop areas
          <div 
            className="h-48 w-full"
            style={getSquiggleCropStyle(template.id, { scaleRange: [3, 4], size: 'quarter' })}
          />
        ) : (
          <img src={template.heroImage} alt={template.title} className="h-48 w-full object-cover brightness-lureo-photo-brightness transition-[scale,filter] duration-lureo-duration-slow ease-lureo-easing-standard group-hover:scale-lureo-photo-hover-scale group-hover:brightness-lureo-photo-hover-brightness" />
        )}
        {/* Category header overlaid on image – gradient fades to transparent so image shows through */}
        {template.category && (
          <div className="absolute top-0 left-0 right-0 z-10 px-4 py-2 bg-lureo-emphasis-strong">
            {/* The first letter of each word capitalised (accents and apostrophes kept as they are); the set's title case can make them capitals */}
            <span className="case-lureo-title-case tracking-lureo-title-tracking font-style-lureo-title-style font-semibold text-lureo-on-emphasis">
              {template.category.replace(/(^|\s)\S/g, start => start.toUpperCase())}
            </span>
          </div>
        )}
        {/* Show appropriate overlay based on image type */}
        {isPlaceholderImage ? (
          <div className="absolute inset-0 opacity-lureo-photo-shade bg-linear-to-t from-lureo-highlight/60 via-lureo-highlight/10 to-transparent" />
        ) : (
          <>
            <div className="absolute inset-0 bg-lureo-photo-veil" />
            <div className="absolute inset-0 opacity-lureo-photo-shade bg-linear-to-t from-lureo-photo-shade-tint/60 via-lureo-photo-shade-tint/10 to-transparent" />
          </>
        )}
        {template.difficulty && (() => {
          const Icon = DIFFICULTY_ICON[template.difficulty];
          return (
            <span
              className={`${PILL_BASE} absolute top-2 right-3 z-10 ${DIFFICULTY_PILL[template.difficulty]}`}
            >
              <Icon className="h-3.5 w-3.5" weight="fill" />
              {template.difficulty.charAt(0).toUpperCase() + template.difficulty.slice(1)}
            </span>
          );
        })()}
        <div className="absolute bottom-4 left-4 right-4">
          <CardTitle as="h3" tone={isPlaceholderImage ? 'accent' : 'emphasis'} className="line-clamp-1 text-lureo-20">
            {template.title}
          </CardTitle>
        </div>
      </div>

      <div className="flex flex-1 flex-col px-5 pb-5 pt-1 sm:px-7 sm:pb-7">
        <div className="grow">
          <p className="line-clamp-2 min-h-12 text-lureo-16 font-lureo-font-ui font-light text-lureo-ink leading-6">{template.description}</p>
        </div>
      </div>
    </Card>
  );
}

